import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Text,
  VStack,
  Container,
  Heading,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { TEXTS } from "../constants/texts";
import TopA from "../images/TopAOSAKA.jpg";
import TopB from "../images/TopBOSAKA.jpg";
// Hashed image manifest generated by scripts/optimize-images.mjs
// Note: CRA allows importing JSON without extra config
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import optimizedManifest from "../optimized/manifest.json";
import HaikeiA from "../images/Background/HaikeiA.jpg";
import RoyalAlbertHall from "../images/Assets/RoyalAlbertHall.jpg";
import PRmovieThumbnail2 from "../images/Thumbnail/PRmovieThumbnail2.jpg";
import { videoConfig } from "../config/videoConfig";

const colors = {
  gold: "rgb(233, 203, 141)",
};

const preventDefault = (e: React.MouseEvent): void => {
  e.preventDefault();
};

const Trailer: React.FC = () => {
  // スクロール方向を検出
  const prevScrollY = useRef(0);
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTimeRef = useRef(0);

  // HLS動画のURL
  const hlsVideoUrl = videoConfig.hlsVideoUrl;

  // ビデオ要素の参照（動画非表示のため未使用）
  // const videoElementRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);
  const hlsInstanceRef = useRef<any>(null);

  // useInView設定の最適化
  const useInViewConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.15,
      rootMargin: "10% 0px",
    }),
    []
  );

  // RoyalAlbertHall用のInView設定（より遠い距離から検出）
  const useInViewRoyalAlbertConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.15, // 閾値を下げて早めに検出
      rootMargin: "10% 0px", // 上下に余裕を持たせる
    }),
    []
  );

  // タイトルテキスト用のInView設定（実際に画面に表示されてから検出）
  const useInViewTextConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.15, // 閾値を下げて早めに検出
      rootMargin: "10% 0px", // 上下に余裕を持たせる
    }),
    []
  );

  // コンポーネント全体がビューポートに入ったかどうかを検出
  const { ref: componentRef } = useInView({
    triggerOnce: false,
    threshold: 0.05,
    rootMargin: "0px 0px 150px 0px",
  });

  // useInViewフック（動画）
  const { ref: videoRef, inView: videoInView } = useInView(useInViewConfig);
  const { ref: textRef, inView: textInView } = useInView(useInViewTextConfig);
  const { ref: royalAlbertRef, inView: royalAlbertInView } = useInView(
    useInViewRoyalAlbertConfig
  );
  const { ref: descriptionRef, inView: descriptionInView } =
    useInView(useInViewConfig);

  // アニメーション状態（動画）
  const [videoAnimationState, setVideoAnimationState] = useState("hidden");
  const [textAnimationState, setTextAnimationState] = useState("hidden");
  const [royalAlbertAnimationState, setRoyalAlbertAnimationState] =
    useState("hidden");
  const [descriptionAnimationState, setDescriptionAnimationState] =
    useState("hidden");

  // ヒーロー画像のアスペクト比を実画像に合わせて動的に確保（初期値は16:9）
  const [heroPaddingTopPercent, setHeroPaddingTopPercent] =
    useState<string>("56.25%");

  // スクロール方向検出とアニメーション状態の更新を統合
  useEffect(() => {
    // 初期スクロール位置を設定
    prevScrollY.current = window.scrollY;

    // モバイルデバイス検出
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const throttleTime = isMobile ? 16 : 50; // モバイルではより高速に反応（約60fps）

    const handleScroll = () => {
      // スロットリング実装
      const now = Date.now();
      if (now - lastScrollTimeRef.current < throttleTime) {
        // 前回のスクロール処理からthrottleTime経っていない場合はスキップ
        if (scrollTimerRef.current === null) {
          // 次回の処理をスケジュール
          scrollTimerRef.current = setTimeout(() => {
            lastScrollTimeRef.current = Date.now();
            scrollTimerRef.current = null;
            handleScroll();
          }, throttleTime);
        }
        return;
      }

      lastScrollTimeRef.current = now;
      const currentScrollY = window.scrollY;
      const previousY = prevScrollY.current;
      const diff = currentScrollY - previousY;
      const isScrollingDown = diff > 0;

      // スクロール方向を更新（小さな変動は無視）
      if (Math.abs(diff) > 2) {
        const newDirection = isScrollingDown ? "down" : "up";

        // 方向が変わった場合、または下スクロールの場合は常に状態を更新
        if (newDirection !== scrollDirection || newDirection === "down") {
          setScrollDirection(newDirection);
        }
      }

      prevScrollY.current = currentScrollY;
    };

    // スクロールイベントをより頻繁に検出
    window.addEventListener("scroll", handleScroll, { passive: true });

    // タッチイベントも監視（モバイル向け）
    if (isMobile) {
      let touchStartY = 0;

      const handleTouchStart = (e: TouchEvent) => {
        touchStartY = e.touches[0].clientY;
      };

      const handleTouchMove = (e: TouchEvent) => {
        const touchY = e.touches[0].clientY;
        const diff = touchStartY - touchY;

        // 明確なスワイプを検出した場合のみ方向を更新
        if (Math.abs(diff) > 10) {
          setScrollDirection(diff > 0 ? "down" : "up");
          touchStartY = touchY;
        }
      };

      window.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", handleTouchMove, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
      };
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [scrollDirection]); // scrollDirectionの依存性を追加

  // ビデオアニメーション状態の更新
  useEffect(() => {
    if (videoInView) {
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setVideoAnimationState("instant");
        } else {
          setVideoAnimationState("hidden");
          requestAnimationFrame(() => {
            setVideoAnimationState("visible");
          });
        }
      });
    } else if (scrollDirection === "up") {
      setVideoAnimationState("hidden");
    }
  }, [videoInView, scrollDirection]);

  // テキストアニメーション状態の更新
  useEffect(() => {
    if (textInView) {
      requestAnimationFrame(() => {
        setTextAnimationState(scrollDirection === "up" ? "instant" : "visible");
      });
    } else if (scrollDirection === "down") {
      setTextAnimationState("hidden");
    }
  }, [textInView, scrollDirection]);

  // ロイヤルアルバートホールアニメーション状態の更新
  useEffect(() => {
    if (royalAlbertInView) {
      requestAnimationFrame(() => {
        setRoyalAlbertAnimationState(
          scrollDirection === "up" ? "instant" : "visible"
        );
      });
    } else if (scrollDirection === "down") {
      setRoyalAlbertAnimationState("hidden");
    }
  }, [royalAlbertInView, scrollDirection]);

  // 説明セクションアニメーション状態の更新
  useEffect(() => {
    if (descriptionInView) {
      requestAnimationFrame(() => {
        setDescriptionAnimationState(
          scrollDirection === "up" ? "instant" : "visible"
        );
      });
    } else if (scrollDirection === "down") {
      setDescriptionAnimationState("hidden");
    }
  }, [descriptionInView, scrollDirection]);

  // 画像選択（レスポンシブ）
  const topImage = useBreakpointValue({
    base: TopB,
    md: TopA,
  });

  // Build hashed srcset strings from manifest
  const desktopAvifSrcSet = useMemo(() => {
    try {
      const a = (optimizedManifest as any)["TopA"].avif;
      return `/optimized/TopA/${a["768"]} 768w, /optimized/TopA/${a["1280"]} 1280w, /optimized/TopA/${a["1920"]} 1920w`;
    } catch {
      return "/optimized/TopA/TopA-768.avif 768w, /optimized/TopA/TopA-1280.avif 1280w, /optimized/TopA/TopA-1920.avif 1920w";
    }
  }, []);
  const desktopWebpSrcSet = useMemo(() => {
    try {
      const a = (optimizedManifest as any)["TopA"].webp;
      return `/optimized/TopA/${a["768"]} 768w, /optimized/TopA/${a["1280"]} 1280w, /optimized/TopA/${a["1920"]} 1920w`;
    } catch {
      return "/optimized/TopA/TopA-768.webp 768w, /optimized/TopA/TopA-1280.webp 1280w, /optimized/TopA/TopA-1920.webp 1920w";
    }
  }, []);
  const mobileAvifSrcSet = useMemo(() => {
    try {
      const b = (optimizedManifest as any)["TopB"].avif;
      return `/optimized/TopB/${b["480"]} 480w, /optimized/TopB/${b["768"]} 768w, /optimized/TopB/${b["1080"]} 1080w`;
    } catch {
      return "/optimized/TopB/TopB-480.avif 480w, /optimized/TopB/TopB-768.avif 768w, /optimized/TopB/TopB-1080.avif 1080w";
    }
  }, []);
  const mobileWebpSrcSet = useMemo(() => {
    try {
      const b = (optimizedManifest as any)["TopB"].webp;
      return `/optimized/TopB/${b["480"]} 480w, /optimized/TopB/${b["768"]} 768w, /optimized/TopB/${b["1080"]} 1080w`;
    } catch {
      return "/optimized/TopB/TopB-480.webp 480w, /optimized/TopB/TopB-768.webp 768w, /optimized/TopB/TopB-1080.webp 1080w";
    }
  }, []);

  // アニメーションの方向をレスポンシブに設定
  const animationDirection = useBreakpointValue({
    base: { x: 0, y: 30 },
    md: { x: 0, y: 0 },
  });

  // スライドインアニメーションのバリエーション
  const slideInVariants = {
    hidden: {
      opacity: 0,
      ...animationDirection,
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 1.2,
        ease: "easeOut",
        delay: 0,
      },
    },
    // 上方向スクロール用の即時表示
    instant: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  // 明示的にメインテキスト列を定義（順序の安定と重複防止）
  const mainTexts = useMemo(
    () => [
      TEXTS.CONTENT,
      TEXTS.MAIN_CONTENT_2,
      TEXTS.MAIN_CONTENT_3,
      TEXTS.MAIN_CONTENT_4,
      TEXTS.MAIN_CONTENT_5,
      TEXTS.MAIN_CONTENT_6,
      TEXTS.MAIN_CONTENT_7,
      TEXTS.MAIN_CONTENT_8,
      TEXTS.MAIN_CONTENT_9,
      TEXTS.MAIN_CONTENT_10,
      TEXTS.MAIN_CONTENT_11,
      TEXTS.MAIN_CONTENT_12,
    ],
    []
  );

  // HLSビデオのアニメーション設定
  const videoFadeVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        delay: 0.2,
      },
    },
    instant: {
      opacity: 1,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.0,
        ease: "easeOut",
        delay: 0.1,
      },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  // ビデオプレーヤーの初期化（動画エリアが視認範囲に入ったら動的 import）
  useEffect(() => {
    let mounted = true;
    // video.js の名残の player 変数は不要
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let player: any = null;
    const initializedRef = { current: !!playerRef.current } as {
      current: boolean;
    };

    // iOS Safari 対応: 再生終了後に currentTime を 0 に戻す
    const onVideoLoadedMetadata = (e: Event) => {
      try {
        const el = e.currentTarget as HTMLVideoElement;
        el.volume = 0.5;
      } catch {}
    };
    const onVideoEnded = (e: Event) => {
      try {
        const el = e.currentTarget as HTMLVideoElement;
        // 再生終了後に次の再生で先頭から始まるよう位置をリセット
        el.pause();
        el.currentTime = 0;
      } catch {}
    };

    const initializePlayer = () => {
      if (!mounted) return;

      const videoElement = document.getElementById(
        "trailer-video"
      ) as HTMLVideoElement;
      if (!videoElement) return;

      // 既存のプレーヤー/インスタンスがある場合は破棄
      if (
        playerRef.current &&
        typeof playerRef.current.dispose === "function"
      ) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      if (hlsInstanceRef.current) {
        try {
          hlsInstanceRef.current.destroy();
        } catch {}
        hlsInstanceRef.current = null;
      }

      try {
        // 動的に hls.js を読み込み（Safari 等 HLS ネイティブ対応は<video>のみ）
        const loadPlayer = async () => {
          const canNativeHls = !!document
            .createElement("video")
            .canPlayType("application/vnd.apple.mpegURL");

          // 基本設定
          videoElement.controls = true;
          videoElement.volume = 0.5;
          videoElement.preload = "metadata";

          if (canNativeHls) {
            // Safari 等
            videoElement.src = hlsVideoUrl;
            playerRef.current = videoElement;
          } else {
            const hlsModule: any = await import("hls.js");
            const Hls = hlsModule.default || hlsModule;
            if (Hls.isSupported()) {
              const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: false,
                backBufferLength: 90,
              });
              hls.loadSource(hlsVideoUrl);
              hls.attachMedia(videoElement);
              hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                // 自動再生はしない
              });
              hlsInstanceRef.current = hls;
              playerRef.current = videoElement;
            } else {
              // 非対応ブラウザはMP4等のフォールバックを検討
              console.warn("HLS not supported in this browser.");
            }
          }

          // シンプルなイベントワイヤリング（解析/計測向け）
          videoElement.addEventListener(
            "loadedmetadata",
            onVideoLoadedMetadata
          );
          // iOS Safari: 再生終了後に再生ボタンを押しても先頭に戻らない問題の対策
          videoElement.addEventListener("ended", onVideoEnded);
        };

        loadPlayer();
      } catch (error) {
        console.error("Error initializing video player:", error);
      }
    };

    // マウント後に初期化（idle優先）。動画の可視制御はアニメーション側で実施
    const initTimer: any = setTimeout(() => {
      if (!initializedRef.current) {
        if ("requestIdleCallback" in window) {
          // @ts-ignore
          window.requestIdleCallback(
            () => {
              initializePlayer();
              initializedRef.current = true;
            },
            { timeout: 1000 }
          );
        } else {
          initializePlayer();
          initializedRef.current = true;
        }
      }
    }, 300);

    // クリーンアップ関数
    return () => {
      mounted = false;
      if (initTimer) clearTimeout(initTimer);

      // イベントリスナーのクリーンアップ
      const el = document.getElementById(
        "trailer-video"
      ) as HTMLVideoElement | null;
      if (el) {
        el.removeEventListener(
          "loadedmetadata",
          onVideoLoadedMetadata as EventListener
        );
        el.removeEventListener("ended", onVideoEnded as EventListener);
      }

      if (
        playerRef.current &&
        typeof playerRef.current.dispose === "function"
      ) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      if (hlsInstanceRef.current) {
        try {
          hlsInstanceRef.current.destroy();
        } catch {}
        hlsInstanceRef.current = null;
      }
    };
  }, [hlsVideoUrl]);

  // HLSファイルの可用性チェック
  useEffect(() => {
    // HLSファイルにアクセスできるかチェック
    const checkHlsAvailability = async () => {
      try {
        const response = await fetch(hlsVideoUrl, { method: "HEAD" });
        if (!response.ok) {
          // エラー処理
        }
      } catch (error) {
        // エラー処理
      }
    };

    checkHlsAvailability();
  }, [hlsVideoUrl]);

  // video.js 用のスタイル調整は不要になったため削除

  return (
    <Box
      pos="relative"
      overflow="hidden"
      ref={componentRef}
      sx={{ willChange: "transform, opacity", backfaceVisibility: "hidden" }}
    >
      {/* トップ画像 */}
      <Box
        position="relative"
        w="100%"
        sx={{
          "&::before": {
            content: '""',
            display: "block",
            paddingTop: heroPaddingTopPercent, // 実画像の比率に追従
          },
        }}
      >
        <picture>
          {/* Desktop sources (md and up) */}
          <source
            media="(min-width: 48em)"
            type="image/avif"
            srcSet={desktopAvifSrcSet}
            sizes="100vw"
          />
          <source
            media="(min-width: 48em)"
            type="image/webp"
            srcSet={desktopWebpSrcSet}
            sizes="100vw"
          />
          {/* Mobile sources (below md) */}
          <source
            media="(max-width: 47.99em)"
            type="image/avif"
            srcSet={mobileAvifSrcSet}
            sizes="100vw"
          />
          <source
            media="(max-width: 47.99em)"
            type="image/webp"
            srcSet={mobileWebpSrcSet}
            sizes="100vw"
          />
          <img
            src={topImage as string}
            alt="top main"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (img.naturalWidth && img.naturalHeight) {
                const ratio = (img.naturalHeight / img.naturalWidth) * 100;
                setHeroPaddingTopPercent(`${ratio}%`);
              }
            }}
            onContextMenu={preventDefault}
            draggable={false}
          />
        </picture>
      </Box>

      {/* メインコンテンツエリア */}
      <Box position="relative">
        {/* 背景画像 - 固定高さを削除して、コンテンツに合わせて自動的に高さを調整 */}
        <Box position="relative">
          <picture>
            <source
              type="image/avif"
              srcSet="/optimized/HaikeiA/HaikeiA-768.avif 768w, /optimized/HaikeiA/HaikeiA-1280.avif 1280w, /optimized/HaikeiA/HaikeiA-1920.avif 1920w"
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet="/optimized/HaikeiA/HaikeiA-768.webp 768w, /optimized/HaikeiA/HaikeiA-1280.webp 1280w, /optimized/HaikeiA/HaikeiA-1920.webp 1920w"
              sizes="100vw"
            />
            <Image
              src={HaikeiA}
              alt="Background"
              w="100%"
              objectFit="cover"
              objectPosition="center bottom"
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              minHeight="100%"
              zIndex="-1"
            />
          </picture>

          <Box
            width="100%"
            px={{
              base: "5%",
              sm: "8%",
              md: "10%",
              lg: "12%",
            }}
            py={{
              base: "40px",
              sm: "50px",
              md: "60px",
              lg: "80px",
            }}
            id="trailer-title" // HLS動画が再度有効になった場合は、trailer-video要素に戻す
          >
            <VStack
              spacing={{
                base: "30px",
                sm: "40px",
                md: "50px",
                lg: "60px",
              }}
              align="center"
            >
              {/* HLS動画 */}
              <motion.div
                ref={videoRef}
                initial="hidden"
                animate={videoAnimationState}
                variants={videoFadeVariants}
                style={{ width: "100%" }}
              >
                <Box
                  w="100%"
                  maxW={{
                    base: "80%",
                    sm: "80%",
                    md: "70%",
                    lg: "70%",
                  }}
                  mx="auto"
                  position="relative"
                  borderRadius={{
                    base: "10px",
                    sm: "15px",
                    md: "20px",
                    lg: "25px",
                  }}
                  overflow="hidden"
                  sx={{
                    "&::before": {
                      content: '""',
                      display: "block",
                      paddingTop: "56.25%", // 16:9のアスペクト比
                    },
                  }}
                >
                  <video
                    id="trailer-video"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "block",
                      backgroundColor: "#000",
                    }}
                    controls
                    preload="metadata"
                    playsInline
                    poster={PRmovieThumbnail2}
                  ></video>
                </Box>
              </motion.div>

              {/* テキストセクション */}
              <motion.div
                ref={textRef}
                initial="hidden"
                animate={textAnimationState}
                variants={titleVariants}
                style={{ width: "100%" }}
              >
                <VStack
                  spacing={{
                    base: 3,
                    sm: 4,
                    md: 5,
                  }}
                  width="100%"
                  px={{
                    base: 0,
                    sm: 2,
                  }}
                  mx="auto"
                  maxW={{
                    base: "100%",
                    sm: "100%",
                  }}
                >
                  <motion.div
                    key={`title-1`}
                    initial="hidden"
                    animate={textAnimationState}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: "-10px",
                        sm: "-15px",
                        md: "-15px",
                        lg: "-15px",
                        "2xl": "-15px",
                      }}
                    >
                      {TEXTS.TOPGUN_TITLE}
                    </Text>
                  </motion.div>

                  <motion.div
                    key={`title-2`}
                    initial="hidden"
                    animate={textAnimationState}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: "-10px",
                        sm: "-15px",
                        md: "-15px",
                        lg: "-15px",
                        "2xl": "-15px",
                      }}
                    >
                      {TEXTS.TOPGUN_TITLE_2}
                    </Text>
                  </motion.div>

                  <motion.div
                    key={`title-3`}
                    initial="hidden"
                    animate={textAnimationState}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: 4,
                        sm: 5,
                        md: 6,
                        lg: 8,
                      }}
                    >
                      {TEXTS.TOPGUN_TITLE_3}
                    </Text>
                  </motion.div>

                  {/* 残りのテキスト（明示的な配列で重複防止） */}
                  <VStack spacing={2} width="100%">
                    {mainTexts.map((content, i) => (
                      <motion.div
                        key={`main-text-${i}`}
                        initial="hidden"
                        animate={textAnimationState}
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              duration: 1.0,
                              delay: 0.1 + i * 0.1,
                            },
                          },
                          instant: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0 },
                          },
                        }}
                      >
                        <Text
                          color="white"
                          fontSize={{
                            base: "10px",
                            sm: "11px",
                            md: "15px",
                            lg: "16px",
                            xl: "20px",
                            "2xl": "22px",
                          }}
                          textAlign="center"
                          lineHeight={{
                            base: "1.3",
                            sm: "1.3",
                            md: "1.3",
                            lg: "1.3",
                            xl: "1.3",
                            "2xl": "1.3",
                          }}
                          width="100%"
                          wordBreak={{ base: "break-all", md: "keep-all" }}
                          overflowWrap={{ base: "anywhere", md: "break-word" }}
                          whiteSpace={{ base: "pre-line", xl: "normal" }}
                          letterSpacing={{ base: "-0.01em", sm: "normal" }}
                          mb={
                            i === 3 || i === 6 || i === 9
                              ? { base: 3, sm: 3, md: 4, lg: 5, xl: 6 }
                              : undefined
                          }
                          px={{ base: "1", sm: "0" }}
                        >
                          {content}
                        </Text>
                      </motion.div>
                    ))}
                  </VStack>
                </VStack>
              </motion.div>

              {/* ロイヤルアルバートホール画像 */}
              <Box width="100%" textAlign="center">
                <Box
                  width={{
                    base: "75%",
                    sm: "75%",
                    md: "70%",
                    lg: "70%",
                  }}
                  height="auto"
                  overflow="hidden"
                  mx="auto"
                  display="block"
                  ref={royalAlbertRef}
                >
                  <motion.div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      transformOrigin: "center",
                    }}
                    initial="hidden"
                    animate={royalAlbertAnimationState}
                    variants={{
                      hidden: {
                        opacity: 0,
                        scale: 1.2,
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 1.5,
                          ease: "easeOut",
                        },
                      },
                      instant: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 0,
                        },
                      },
                    }}
                  >
                    <picture style={{ display: "block", width: "100%" }}>
                      <source
                        type="image/avif"
                        srcSet="/optimized/RoyalAlbertHall/RoyalAlbertHall-600.avif 600w, /optimized/RoyalAlbertHall/RoyalAlbertHall-900.avif 900w, /optimized/RoyalAlbertHall/RoyalAlbertHall-1200.avif 1200w"
                        sizes="(min-width: 1200px) 70%, (min-width: 992px) 70%, 75%"
                      />
                      <source
                        type="image/webp"
                        srcSet="/optimized/RoyalAlbertHall/RoyalAlbertHall-600.webp 600w, /optimized/RoyalAlbertHall/RoyalAlbertHall-900.webp 900w, /optimized/RoyalAlbertHall/RoyalAlbertHall-1200.webp 1200w"
                        sizes="(min-width: 1200px) 70%, (min-width: 992px) 70%, 75%"
                      />
                      <img
                        src={RoyalAlbertHall as string}
                        alt="RoyalAlbertHall"
                        loading="eager"
                        decoding="async"
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                        onContextMenu={preventDefault}
                        draggable={false}
                      />
                    </picture>
                  </motion.div>
                </Box>
              </Box>

              {/* 説明セクション */}
              <motion.div
                ref={descriptionRef}
                initial="hidden"
                animate={descriptionAnimationState}
                variants={slideInVariants}
                style={{ width: "100%" }}
              >
                <Container
                  maxW={{
                    base: "100%",
                    sm: "100%",
                    md: "100%",
                    lg: "100%",
                    xl: "93%",
                    "2xl": "80%",
                  }}
                  mb={{
                    base: "20px",
                    sm: "30px",
                    md: "40px",
                    lg: "50px",
                  }}
                  px={{
                    base: "2",
                    sm: undefined,
                    md: undefined,
                  }}
                >
                  <Box
                    borderColor={colors.gold}
                    borderLeft={`2px solid ${colors.gold}`}
                    borderRight={`2px solid ${colors.gold}`}
                    borderBottom={`2px solid ${colors.gold}`}
                    p={{
                      base: 3,
                      sm: 5,
                      md: 6,
                      lg: 8,
                    }}
                    pos="relative"
                    pt={{
                      base: "30px",
                      sm: "35px",
                      md: "40px",
                      lg: "50px",
                    }}
                    sx={{
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: {
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                        },
                        width: {
                          base: "30%",
                          sm: "30%",
                          md: "30%",
                          lg: "30%",
                        },
                        height: "2px",
                        background: colors.gold,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        right: {
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                        },
                        width: {
                          base: "30%",
                          sm: "30%",
                          md: "30%",
                          lg: "30%",
                        },
                        height: "2px",
                        background: colors.gold,
                      },
                    }}
                  >
                    <Heading
                      pos="absolute"
                      top="0"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      px={{
                        base: 2,
                        sm: 3,
                        md: 4,
                      }}
                      py={{
                        base: 1,
                        sm: 1.5,
                        md: 2,
                      }}
                      color="white"
                      fontSize={{
                        base: "13px",
                        sm: "15px",
                        md: "20px",
                        lg: "30px",
                        xl: "32px",
                        "2xl": "36px",
                      }}
                      whiteSpace={{
                        base: "normal",
                        md: "nowrap",
                      }}
                      textAlign="center"
                      minWidth="fit-content"
                    >
                      <motion.span
                        initial="hidden"
                        animate={descriptionAnimationState}
                        variants={titleVariants}
                        style={{ display: "inline-block" }}
                      >
                        {TEXTS.Description.title}
                      </motion.span>
                      <Text
                        as="span"
                        fontSize={{
                          base: "8px",
                          sm: "8px",
                          md: "8px",
                          lg: "21px",
                          xl: "20px",
                          "2xl": "20px",
                        }}
                        position="relative"
                        top={{
                          base: "-5px",
                          sm: "-6px",
                          md: "-8px",
                          lg: "-10px",
                          "2xl": "-12px",
                        }}
                      >
                        <motion.span
                          initial="hidden"
                          animate={descriptionAnimationState}
                          variants={titleVariants}
                          style={{ display: "inline-block" }}
                        >
                          {TEXTS.Description.title_2}
                        </motion.span>
                      </Text>
                      <motion.span
                        initial="hidden"
                        animate={descriptionAnimationState}
                        variants={titleVariants}
                        style={{ display: "inline-block" }}
                      >
                        {TEXTS.Description.title_3}
                      </motion.span>
                    </Heading>
                    <VStack spacing={0}>
                      {TEXTS.Description.content.map((text, index) => (
                        <motion.div
                          key={index}
                          initial="hidden"
                          animate={descriptionAnimationState}
                          variants={{
                            hidden: { opacity: 0, y: 20 },
                            visible: {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 1.0,
                                delay: 0.1 + index * 0.1,
                              },
                            },
                            instant: {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 0,
                              },
                            },
                          }}
                          style={{ width: "100%" }}
                        >
                          <Text
                            key={index}
                            color="white"
                            fontSize={{
                              base: index === 2 ? "8px" : "11px",
                              sm: index === 2 ? "10px" : "11px",
                              md: index === 2 ? "12px" : "15px",
                              lg: index === 2 ? "14px" : "18px",
                              xl: index === 2 ? "16px" : "20px",
                              "2xl": index === 2 ? "16px" : "22px",
                            }}
                            textAlign="center"
                            mt={
                              index === 2
                                ? {
                                    base: 3,
                                    sm: 4,
                                    md: 5,
                                    lg: 6,
                                  }
                                : 0
                            }
                            whiteSpace={{
                              base: "normal",
                              md: "pre-line",
                            }}
                            wordBreak="keep-all"
                            overflowWrap="break-word"
                            lineHeight={{
                              base: "1.5",
                              sm: undefined,
                              md: undefined,
                            }}
                            letterSpacing={{
                              base: "-0.01em",
                              sm: undefined,
                            }}
                          >
                            {text}
                          </Text>
                        </motion.div>
                      ))}
                    </VStack>
                  </Box>
                </Container>
              </motion.div>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Trailer;
